// src/features/analytics/regionLabels.ts
//
// Humanized labels for the region audience dimension. Since Phase 7 Seq U-3
// (notes ADR-015) the wire carries the country-qualified FULL ISO-3166-2 code
// ('US-CA', 'CA-ON', 'IE-L', 'IN-PB') — bare suffixes collided across
// countries in production and were fixed at materialization, so the 3.2.0
// bare-code map and its US/CA-first collision policy are obsolete and gone.
//
// Display tiers:
//   1. Subdivision names for the audience-heavy countries (US, CA, GB, AU,
//      IE provinces, IN states — both pre- and post-2023 ISO codes for the
//      IN renames, whichever the GeoIP dataset emits — and ID provinces,
//      added in Seq U-5 for a live account's Indonesian audience).
//   2. Anything else that parses as CC-SUFFIX renders 'SUFFIX (Country name)'
//      via Intl.DisplayNames (built into every supported browser — no bundled
//      country map; refines ADR-015 item 5's static-map suggestion).
//   3. '' renders 'Unknown'; anything unparseable falls back to the raw code.

const REGION_LABELS: Record<string, string> = {
  // United States
  'US-AL': 'Alabama', 'US-AK': 'Alaska', 'US-AZ': 'Arizona', 'US-AR': 'Arkansas',
  'US-CA': 'California', 'US-CO': 'Colorado', 'US-CT': 'Connecticut', 'US-DE': 'Delaware',
  'US-FL': 'Florida', 'US-GA': 'Georgia', 'US-HI': 'Hawaii', 'US-ID': 'Idaho',
  'US-IL': 'Illinois', 'US-IN': 'Indiana', 'US-IA': 'Iowa', 'US-KS': 'Kansas',
  'US-KY': 'Kentucky', 'US-LA': 'Louisiana', 'US-ME': 'Maine', 'US-MD': 'Maryland',
  'US-MA': 'Massachusetts', 'US-MI': 'Michigan', 'US-MN': 'Minnesota', 'US-MS': 'Mississippi',
  'US-MO': 'Missouri', 'US-MT': 'Montana', 'US-NE': 'Nebraska', 'US-NV': 'Nevada',
  'US-NH': 'New Hampshire', 'US-NJ': 'New Jersey', 'US-NM': 'New Mexico', 'US-NY': 'New York',
  'US-NC': 'North Carolina', 'US-ND': 'North Dakota', 'US-OH': 'Ohio', 'US-OK': 'Oklahoma',
  'US-OR': 'Oregon', 'US-PA': 'Pennsylvania', 'US-RI': 'Rhode Island',
  'US-SC': 'South Carolina', 'US-SD': 'South Dakota', 'US-TN': 'Tennessee', 'US-TX': 'Texas',
  'US-UT': 'Utah', 'US-VT': 'Vermont', 'US-VA': 'Virginia', 'US-WA': 'Washington',
  'US-WV': 'West Virginia', 'US-WI': 'Wisconsin', 'US-WY': 'Wyoming',
  'US-DC': 'District of Columbia', 'US-PR': 'Puerto Rico', 'US-GU': 'Guam',
  'US-VI': 'U.S. Virgin Islands', 'US-AS': 'American Samoa',
  'US-MP': 'Northern Mariana Islands',
  // Canada
  'CA-AB': 'Alberta', 'CA-BC': 'British Columbia', 'CA-MB': 'Manitoba',
  'CA-NB': 'New Brunswick', 'CA-NL': 'Newfoundland and Labrador', 'CA-NS': 'Nova Scotia',
  'CA-NT': 'Northwest Territories', 'CA-NU': 'Nunavut', 'CA-ON': 'Ontario',
  'CA-PE': 'Prince Edward Island', 'CA-QC': 'Quebec', 'CA-SK': 'Saskatchewan',
  'CA-YT': 'Yukon',
  // United Kingdom
  'GB-ENG': 'England', 'GB-SCT': 'Scotland', 'GB-WLS': 'Wales', 'GB-NIR': 'Northern Ireland',
  // Australia (country-qualified codes are unambiguous — WA/SA/NT now safe to name)
  'AU-NSW': 'New South Wales', 'AU-QLD': 'Queensland', 'AU-VIC': 'Victoria',
  'AU-TAS': 'Tasmania', 'AU-ACT': 'Australian Capital Territory',
  'AU-WA': 'Western Australia', 'AU-SA': 'South Australia', 'AU-NT': 'Northern Territory',
  // Ireland (provinces; counties fall through to the Intl tier)
  'IE-L': 'Leinster', 'IE-C': 'Connacht', 'IE-M': 'Munster', 'IE-U': 'Ulster',
  // India (states and union territories)
  'IN-AP': 'Andhra Pradesh', 'IN-AR': 'Arunachal Pradesh', 'IN-AS': 'Assam', 'IN-BR': 'Bihar',
  'IN-CT': 'Chhattisgarh', 'IN-CG': 'Chhattisgarh', 'IN-GA': 'Goa', 'IN-GJ': 'Gujarat',
  'IN-HR': 'Haryana', 'IN-HP': 'Himachal Pradesh', 'IN-JH': 'Jharkhand', 'IN-KA': 'Karnataka',
  'IN-KL': 'Kerala', 'IN-MP': 'Madhya Pradesh', 'IN-MH': 'Maharashtra', 'IN-MN': 'Manipur',
  'IN-ML': 'Meghalaya', 'IN-MZ': 'Mizoram', 'IN-NL': 'Nagaland', 'IN-OR': 'Odisha',
  'IN-OD': 'Odisha', 'IN-PB': 'Punjab', 'IN-RJ': 'Rajasthan', 'IN-SK': 'Sikkim',
  'IN-TN': 'Tamil Nadu', 'IN-TG': 'Telangana', 'IN-TS': 'Telangana', 'IN-TR': 'Tripura',
  'IN-UP': 'Uttar Pradesh', 'IN-UT': 'Uttarakhand', 'IN-UK': 'Uttarakhand',
  'IN-WB': 'West Bengal', 'IN-AN': 'Andaman and Nicobar Islands', 'IN-CH': 'Chandigarh',
  'IN-DH': 'Dadra and Nagar Haveli and Daman and Diu', 'IN-DL': 'Delhi',
  'IN-JK': 'Jammu and Kashmir', 'IN-LA': 'Ladakh', 'IN-LD': 'Lakshadweep',
  'IN-PY': 'Puducherry',
  // Indonesia (provinces, incl. the 2022 Papua splits)
  'ID-AC': 'Aceh', 'ID-BA': 'Bali', 'ID-BB': 'Bangka Belitung Islands', 'ID-BE': 'Bengkulu',
  'ID-BT': 'Banten', 'ID-GO': 'Gorontalo', 'ID-JA': 'Jambi', 'ID-JB': 'West Java',
  'ID-JI': 'East Java', 'ID-JK': 'Jakarta', 'ID-JT': 'Central Java',
  'ID-KB': 'West Kalimantan', 'ID-KI': 'East Kalimantan', 'ID-KR': 'Riau Islands',
  'ID-KS': 'South Kalimantan', 'ID-KT': 'Central Kalimantan', 'ID-KU': 'North Kalimantan',
  'ID-LA': 'Lampung', 'ID-MA': 'Maluku', 'ID-MU': 'North Maluku',
  'ID-NB': 'West Nusa Tenggara', 'ID-NT': 'East Nusa Tenggara',
  'ID-PA': 'Papua', 'ID-PB': 'West Papua', 'ID-PD': 'Southwest Papua',
  'ID-PE': 'Highland Papua', 'ID-PS': 'South Papua', 'ID-PT': 'Central Papua',
  'ID-RI': 'Riau', 'ID-SA': 'North Sulawesi', 'ID-SB': 'West Sumatra',
  'ID-SG': 'Southeast Sulawesi', 'ID-SN': 'South Sulawesi', 'ID-SR': 'West Sulawesi',
  'ID-SS': 'South Sumatra', 'ID-ST': 'Central Sulawesi', 'ID-SU': 'North Sumatra',
  'ID-YO': 'Yogyakarta',
};

// Intl.DisplayNames is constructed lazily and reused; on the (unsupported-
// engine) construction failure the fallback tier degrades to raw codes.
// Exported: audienceValues uses it to render the country DIMENSION rows as
// full names too ('MA' reads as Massachusetts one tab from a US-states region
// view — it is Morocco).
let countryNames: Intl.DisplayNames | null | undefined;

export function countryName(code: string): string | undefined {
  if (countryNames === undefined) {
    try {
      countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      countryNames = null;
    }
  }
  if (!countryNames) return undefined;
  try {
    const name = countryNames.of(code);
    // Intl returns the input echoed back for unknown codes — treat as unmapped.
    return name && name !== code ? name : undefined;
  } catch {
    return undefined;
  }
}

/** Display label for a stored region value (full ISO-3166-2 code since U-3). */
export function regionLabel(regionCode: string): string {
  if (regionCode === '') return 'Unknown';
  const mapped = REGION_LABELS[regionCode];
  if (mapped) return mapped;
  const dash = regionCode.indexOf('-');
  if (dash > 0 && dash < regionCode.length - 1) {
    const country = countryName(regionCode.slice(0, dash));
    if (country) return `${regionCode.slice(dash + 1)} (${country})`;
  }
  return regionCode;
}
